import { useEffect } from 'react';
import { getActiveCaseContactEmails, getAllActiveCasesList } from 'src/shared/services/ims-api/Cases';
import { IntercomService } from '../services/Intercom';
import { User } from 'src/shared/types/User';
import { publishIntercomAvailability } from 'src/shared/federation/utils/publishIntercomAvailability';

// The profile fields below all come from the single most-recent case, so we only need one
// case for them. The distinct attorney/assistant emails come from a dedicated set-based
// endpoint that aggregates across every active case without the per-case work, keeping
// login fast for company-scoped users who can see tens of thousands of cases.
const INTERCOM_PROFILE_CASE_SAMPLE_SIZE = 1;

export const useIntercomInitializer = (isAuthenticated: boolean, user: User | undefined) => {
  useEffect(() => {
    const initIntercom = async (user: User) => {
      const additionalData: Record<string, any> = {};
      try {
        const [profileCases, contactEmails] = await Promise.all([
          getAllActiveCasesList(undefined, undefined, INTERCOM_PROFILE_CASE_SAMPLE_SIZE),
          getActiveCaseContactEmails(),
        ]);

        if (profileCases && Array.isArray(profileCases) && profileCases.length > 0) {
          additionalData.VIP = profileCases[0].isVIP;
          additionalData['Sensitive Case'] = profileCases[0].isBALSensitive;
          additionalData['Matter Number'] = profileCases[0].matterNumber;
          additionalData['Preferred Name'] = profileCases[0].preferredName;
          additionalData['Job Level Grade'] = profileCases[0].jobLevel;
          additionalData['Business Unit'] = profileCases[0].businessUnit;
        }

        contactEmails?.attorneyEmails?.forEach((email, index) => {
          additionalData[`Attorney${index + 1}`] = email;
        });

        contactEmails?.assistantEmails?.forEach((email, index) => {
          additionalData[`Assistant${index + 1}`] = email;
        });

        IntercomService.initialize(
          {
            INTERCOM_APP_URL: window.FN_CONFIG.INTERCOM_APP_URL,
          },
          user,
          additionalData
        );
      } catch (error) {
        console.error('Error initializing Intercom:', error);
        publishIntercomAvailability(false);
      }
    };

    if (isAuthenticated && user) {
      initIntercom(user);
    } else {
      IntercomService.clearSession();
    }
  }, [isAuthenticated, user]);
};
